import { useEffect, useRef, useState } from "react"

interface UseTypewriterOptions {
  text: string
  // Total duration in ms to reveal the full string. Defaults to 800ms.
  duration?: number
  // When false, returns the full text immediately and skips the animation.
  // Useful for opting out per-call (the hook also auto-skips when the user
  // has prefers-reduced-motion enabled).
  enabled?: boolean
  // Optional identifier that persists "already revealed" state across
  // mount/unmount cycles for the life of the JS module. Two callers that
  // share the same key share the same revealed-strings set, so remount
  // churn (Suspense boundaries, AnimatePresence swaps, StrictMode) doesn't
  // replay the animation for a string that has already been typed once.
  // Omit for call-sites that should animate fresh on every mount.
  persistKey?: string
  // Wait this many ms before the first character (display stays empty).
  // Ignored when reduced motion skips animation.
  startDelayMs?: number
}

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined") return false
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches
}

// Module-level store of text values that have already been typed out for a
// given persistKey. Used to suppress restarts across remounts.
const persistedReveals = new Map<string, Set<string>>()

function hasPersistedReveal(key: string, text: string): boolean {
  return persistedReveals.get(key)?.has(text) ?? false
}

function markPersistedReveal(key: string, text: string): void {
  let set = persistedReveals.get(key)
  if (!set) {
    set = new Set<string>()
    persistedReveals.set(key, set)
  }
  set.add(text)
}

/**
 * Reveals a string character-by-character over `duration` ms. Honors
 * prefers-reduced-motion (returns the full string immediately).
 * Optional `startDelayMs` keeps the display empty before typing begins.
 *
 * Only animates once per distinct `text` value while `enabled` is true. If
 * the effect re-runs after a completed run with the same `text`, the hook
 * keeps the fully-typed string. Turning `enabled` off shows the full string
 * without marking it "revealed" for animation purposes, so turning `enabled`
 * back on replays the reveal (call-sites like the logo use this).
 *
 * Pass `persistKey` to also suppress re-animation across mount/unmount
 * cycles for the life of the module — useful for static placeholders like
 * "Thinking..." that would otherwise retype on every remount.
 */
export function useTypewriter({
  text,
  duration = 800,
  enabled = true,
  persistKey,
  startDelayMs = 0,
}: UseTypewriterOptions) {
  const isAlreadyRevealed = (value: string): boolean =>
    Boolean(persistKey) && hasPersistedReveal(persistKey as string, value)

  const skipRevealAnimation = (value: string): boolean =>
    !value || duration <= 0 || isAlreadyRevealed(value) || prefersReducedMotion()

  // Show full string when not animating: disabled, empty, bad duration, reduced
  // motion, or persisted skip. `enabled: false` still shows full text but must
  // not set revealedRef to `text`, or a later `enabled: true` would skip the run.
  const shouldShowFullImmediately = !enabled || skipRevealAnimation(text)

  const initialDisplay = shouldShowFullImmediately ? text : ""
  const initialProgress = shouldShowFullImmediately && text.length > 0 ? 1 : 0

  const [display, setDisplay] = useState(initialDisplay)
  const [progress, setProgress] = useState(initialProgress)
  const frameRef = useRef<number | null>(null)
  const startTimeRef = useRef<number | null>(null)
  // Tracks the last text value fully revealed by the RAF loop while enabled.
  const revealedRef = useRef<string | null>(enabled && skipRevealAnimation(text) ? text : null)

  useEffect(() => {
    if (prefersReducedMotion()) {
      setDisplay(text)
      setProgress(text.length > 0 ? 1 : 0)
      revealedRef.current = text
      if (persistKey && text) markPersistedReveal(persistKey, text)
      return
    }

    if (!text || duration <= 0) {
      setDisplay(text)
      setProgress(text.length > 0 ? 1 : 0)
      revealedRef.current = text || null
      if (persistKey && text) markPersistedReveal(persistKey, text)
      return
    }

    if (!enabled) {
      setDisplay(text)
      setProgress(text.length > 0 ? 1 : 0)
      revealedRef.current = null
      return
    }

    // Already typed this string (same mount OR persisted across mounts).
    if (revealedRef.current === text || isAlreadyRevealed(text)) {
      setDisplay(text)
      setProgress(text.length > 0 ? 1 : 0)
      revealedRef.current = text
      return
    }

    setDisplay("")
    setProgress(0)
    startTimeRef.current = null

    const animate = (now: number) => {
      if (startTimeRef.current === null) {
        startTimeRef.current = now
      }
      const elapsed = now - startTimeRef.current
      const t = Math.min(elapsed / duration, 1)
      const charsToShow = Math.floor(t * text.length)
      setProgress(t)
      setDisplay(text.slice(0, charsToShow))

      if (t < 1) {
        frameRef.current = requestAnimationFrame(animate)
      } else {
        setDisplay(text)
        setProgress(1)
        revealedRef.current = text
        if (persistKey) markPersistedReveal(persistKey, text)
      }
    }

    let delayTimeout: ReturnType<typeof setTimeout> | null = null

    const runAnimation = () => {
      frameRef.current = requestAnimationFrame(animate)
    }

    if (startDelayMs > 0) {
      delayTimeout = setTimeout(runAnimation, startDelayMs)
    } else {
      runAnimation()
    }

    return () => {
      if (delayTimeout !== null) {
        clearTimeout(delayTimeout)
      }
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current)
        frameRef.current = null
      }
    }
  }, [text, duration, enabled, persistKey, startDelayMs])

  return { display, progress }
}

export type UseTypewriterResult = ReturnType<typeof useTypewriter>
